import React from 'react';
import { FieldRenderProps } from 'react-final-form';
import {
  Box, HStack, Switch, Text, useColorModeValue,
} from '@chakra-ui/react';
import FieldLabel from './FieldLabel';

// item 261 — the shared on/off control for forms. Replaces CheckboxInput's
// label-stacked-over-a-tiny-checkbox (which pushed checkboxes to different heights
// when labels wrapped — the "scattered" look in Chad's screenshots). Here the label
// sits on ONE fixed-height row with the Switch right-aligned, so a grid of these
// always lines up regardless of label length. item 254 — optional `helperText` gives
// each toggle a plain-language explainer; label contrast is legible in both skins.
// label/helperText/required ride in as react-final-form's pass-through props, so they
// aren't part of FieldRenderProps — keep them optional to stay assignable to Field's
// `component` prop (same pattern as CheckboxInput).
type Props = FieldRenderProps<boolean, any> & {
  label?: string;
  helperText?: string;
  required?: boolean;
  // item 332 — set when the toggle sits IN A FIELD ROW beside labeled inputs: renders
  // the FieldLabel-rhythm layout (reserved label zone on top, switch in the input
  // zone) so it shares the row baseline instead of floating above its neighbours.
  field?: boolean;
  // item 710 — lock a toggle on (e.g. the last visible pricing option can't be hidden)
  isDisabled?: boolean;
};

const ToggleInput: React.FC<Props> = ({
  input: { value, onChange }, label, helperText, meta, required, field, isDisabled, ...rest
}) => {
  const labelColor = useColorModeValue('gray.700', 'whiteAlpha.900');
  const helpColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  if (field) {
    return (
      <Box w="full" {...rest}>
        <FieldLabel>
          {label}
          {required ? ' *' : ''}
        </FieldLabel>
        <HStack minH="40px" justify="space-between" spacing={3}>
          {helperText ? (
            <Text fontSize="xs" color={helpColor} lineHeight="short" minW={0}>
              {helperText}
            </Text>
          ) : <Box />}
          <Switch
            isChecked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            isDisabled={isDisabled}
            colorScheme="brandGreen"
            flexShrink={0}
          />
        </HStack>
        {meta?.error && meta?.touched && (
          <Text fontSize="xs" color="red.400" mt={0.5}>{meta.error}</Text>
        )}
      </Box>
    );
  }
  return (
    <HStack
      justify="space-between"
      align="center"
      spacing={3}
      minH="44px"
      w="full"
      {...rest}
    >
      <Box minW={0}>
        <Text fontSize="sm" fontWeight="medium" color={labelColor} lineHeight="short">
          {label}
          {required ? ' *' : ''}
        </Text>
        {helperText && (
          <Text fontSize="xs" color={helpColor} lineHeight="short" mt={0.5}>
            {helperText}
          </Text>
        )}
        {meta?.error && meta?.touched && (
          <Text fontSize="xs" color="red.400" mt={0.5}>{meta.error}</Text>
        )}
      </Box>
      <Switch
        isChecked={Boolean(value)}
        onChange={(e) => onChange(e.target.checked)}
        isDisabled={isDisabled}
        colorScheme="brandGreen"
        flexShrink={0}
      />
    </HStack>
  );
};

ToggleInput.defaultProps = {
  label: '',
  helperText: undefined,
  required: false,
  field: false,
  isDisabled: false,
};

export default ToggleInput;
